import router from '@/router';
import deepEqual from 'deep-equal';
import { RawLocation, Route } from 'vue-router';

export interface Features {
	isCallLogEnabled: boolean;

	/**
	 * Controls whether a portion of the Claim Form is visible, which after implemented was deemed
	 * unnecessary.
	 */
	isClaimFormClaimantPlantiffAttorneyAddressEnabled: boolean;

	isRequestPeerSupportEnabled: boolean;

	/**
	 * Controls whether a staff member and unregistered/unauthenticated user can organized a screen
	 * sharing session. This feature is not yet fully implemented.
	 */
	isScreenSharingWithNonUsersEnabled: boolean;

	/**
	 * Controls whether a staff member can send a screen sharing request to a currently logged in
	 * user.
	 */
	isScreenSharingWithUsersEnabled: boolean;

	/**
	 * Controls whether UMIA customers can download policy documents. COIs are always allowed,
	 * regardless of this setting.
	 */
	isUmiaPolicyDocumentsEnabled: boolean;

	isEmbeddingEnabled: boolean;
}

type FeatureName = keyof Features;

function allFeatureNames(): FeatureName[] {
	// Creating an instance of `Features` and then getting its list of keys is the most reliable
	// way to get the exact list of feature names because the compiler will enforce that this
	// instance stays in sync with `Features`.
	const sample: Features = {
		isCallLogEnabled: false,
		isClaimFormClaimantPlantiffAttorneyAddressEnabled: false,
		isRequestPeerSupportEnabled: false,
		isScreenSharingWithNonUsersEnabled: false,
		isScreenSharingWithUsersEnabled: false,
		isUmiaPolicyDocumentsEnabled: false,
		isEmbeddingEnabled: false
	};
	return Object.keys(sample) as FeatureName[];
}

/**
 * An implementation of `Features` that relies on query parameters and environment variables (in
 * that order of precedence) to toggle features.
 */
class EnvFeatures implements Readonly<Features> {
	public get isCallLogEnabled(): boolean {
		return this.isFeatureEnabled('isCallLogEnabled', 'VUE_APP_FEATURE_CALL_LOG');
	}

	public get isClaimFormClaimantPlantiffAttorneyAddressEnabled(): boolean {
		return this.isFeatureEnabled(
			'isClaimFormClaimantPlantiffAttorneyAddressEnabled',
			'VUE_APP_FEATURE_CLAIM_FORM_CLAIMANT_PLANTIFF_ATTORNEY_ADDRESS',
		);
	}

	public get isRequestPeerSupportEnabled(): boolean {
		return this.isFeatureEnabled(
			'isRequestPeerSupportEnabled',
			'VUE_APP_FEATURE_REQUEST_PEER_SUPPORT',
		);
	}

	public get isEmbeddingEnabled(): boolean {
		return this.isFeatureEnabled(
			'isEmbeddingEnabled',
			'VUE_APP_FEATURE_EMBEDDING_ENABLED',
		);
	}

	public get isScreenSharingWithNonUsersEnabled(): boolean {
		return this.isFeatureEnabled(
			'isScreenSharingWithNonUsersEnabled',
			'VUE_APP_FEATURE_SCREEN_SHARING_WITH_NON_USERS',
		);
	}

	public get isScreenSharingWithUsersEnabled(): boolean {
		return this.isFeatureEnabled(
			'isScreenSharingWithUsersEnabled',
			'VUE_APP_FEATURE_SCREEN_SHARING_WITH_USERS',
		);
	}

	public get isUmiaPolicyDocumentsEnabled(): boolean {
		return this.isFeatureEnabled(
			'isUmiaPolicyDocumentsEnabled',
			'VUE_APP_FEATURE_UMIA_POLICY_DOCUMENTS',
		);
	}

	public static get singleton(): EnvFeatures {
		if (!EnvFeatures._singleton) {
			EnvFeatures._singleton = new EnvFeatures();
			EnvFeatures.preserveFeatureFlagQueryParams();
		}
		return EnvFeatures._singleton;
	}
	private static _singleton: EnvFeatures | undefined;

	private isFeatureEnabled(queryParamName: keyof Features, envVariableName: string): boolean {
		if (window.location.search !== this._cachedSearch) {
			this._cachedSearch = window.location.search;
			this._cachedSearchParams = new URLSearchParams(window.location.search);
		}

		const value: string =
			this._cachedSearchParams.get(queryParamName) || process.env[envVariableName];
		return value === 'true';
	}

	/**
	 * Registers a `NavigationGuard` that preserves query parameters pertaining to feature flags as
	 * the user navigates from route to route.
	 *
	 * This method should be invoked only once ever, and only if an instance of `EnvFeatures` will
	 * be paying attention to query parameters. Therefore, this method is invoked by the
	 * `EnvFeatures.singleton` getter the first time it is executed.
	 */
	private static preserveFeatureFlagQueryParams(): void {
		type QueryParams = Route['query'];

		const featureNames: readonly FeatureName[] = Object.freeze(allFeatureNames());

		router.beforeEach(
			(
				to: Route,
				from: Route,
				next: (to?: RawLocation | false | ((vm: Vue) => unknown) | void) => void,
			): void => {
				// Identify the query params in the `from` route that are feature flags.
				const featureFlagQueryParams: QueryParams = Object.fromEntries(
					Object.entries(from.query).filter(([key, _value]): boolean =>
						(featureNames as string[]).includes(key),
					),
				);

				// Combine the feature flags with `to`'s query params.
				const toWithFeatureFlagQueryParams: Route = {
					...to,
					...{
						query: {
							...featureFlagQueryParams,
							...to.query,
						},
					},
				};

				// Continue the navigation with the newly combined query params. Supplying a new
				// `Route` to `next()` will end up reinvoking this guard, so to prevent an infinite
				// loop, make sure something about the new `Route` is actually different.
				if (!deepEqual(toWithFeatureFlagQueryParams, to)) {
					next(toWithFeatureFlagQueryParams);
				} else {
					next();
				}
			},
		);
	}

	private _cachedSearch: string | undefined;
	private _cachedSearchParams: URLSearchParams | undefined;
}

export const Features = EnvFeatures.singleton;
